import { IImageDragParameter } from ".";
import { FileToBase64TextConverter } from "../document/editor/FileToBase64TextConverter";
import { DomainRegistry } from "../../domain";
import { IDocumentImageSource, ImageKinds } from "../../domain/document";
import { MovableImageItem, IDrawableItem } from "../../domain/document/items";
import { Point } from "../../domain/drawing";
import { IConfiguration } from "../../domain/configuration";
import store from "../../store";
import { Patient } from "../../domain/patient";

// TypeScript上でイベントハンドラ登録するとthisがずれるのでラムダ式で対応している
export class ImageDropManager {
  constructor(
    private _error: (title: string, message: string, error: any) => void,
    private _addItem: (item: IDrawableItem) => void,
    page: HTMLDivElement,
    scale: number,
    event: DragEvent,
    private createImageSource: (imageText: string) => Promise<IDocumentImageSource>,
    private _patient: Patient,
  ) {
    const pageRect = page.getBoundingClientRect();
    const pointerX = Math.round((event.clientX + page.scrollLeft - pageRect.left) / scale);
    const pointerY = Math.round((event.clientY + page.scrollTop - pageRect.top) / scale);
    this._position = new Point(pointerX, pointerY);
    this._text = event.dataTransfer.getData("text");
    this._files = event.dataTransfer.files;
  }

  // 画像を貼り付ける位置
  private _position: Point;

  // 張り付ける内容の情報
  private _text: string;
  private _files: any;

  get claioImageName(): string {
    return (store.getters.configuration as IConfiguration).behavior.collaboration.claio.imageName;
  }

  get schemaName(): string {
    return (store.getters.configuration as IConfiguration).behavior.collaboration.schema.imageName;
  }

  get externalImageName(): string {
    return (store.getters.configuration as IConfiguration).behavior.collaboration.externalImage.imageName;
  }

  get ocojoImageName(): string {
    return (store.getters.configuration as IConfiguration).behavior.collaboration.ocojo.imageName;
  }

  async run(): Promise<void> {
    try {
      if (!this._text) {
        const file = this._files[0];
        if (!file || !file.type || file.type.indexOf("image/") !== 0) {
          return;
        }
        await this.addMovableImageItemFromFile(file);
        return;
      }
      const data = ImageDropManager.parseAsClaioImageDragParameter(this._text);
      if (!data) { return; }
      if (data.isClaioImage) {
        const param = data.claioImageDragParameter;
        if (param.patientId !== this._patient.id) { throw new Error("画像の患者IDが一致していません。"); }
        await this.addMovableImageItemFromClaioImage(param.patientId, param.claioImageId, param.isVisibleLayer, param.isMovie, param.isSecret, param.imageDepartmentId);
      }
      if (data.isClaioSchema) {
        const param = data.claioSchemaDragParameter;
        await this.addMovableImageItemFromClaioSchema(param.schemaTemplateId, param.schemaGroupId, param.schemaItemId);
      }
      if (data.isExternalImage) {
        const param = data.externalImageDragParameter;
        if (param.patientId !== this._patient.id) { throw new Error("画像の患者IDが一致していません。"); }
        await this.addMovableImageItemFromExternalImage(param.patientId, param.externalImageId);
      }
      if (data.isOcojoImage) {
        const param = data.ocojoImageDragParameter;
        if (param.patientId !== this._patient.id) { throw new Error("画像の患者IDが一致していません。"); }
        await this.addMovableImageItemFromOcojoImage(param.patientId, param.imageId, param.isVisibleLayer, param.isMovie, param.isSecret, param.imageDepartmentId);
      }
      if (data.isRegisteredImage) {
        // 何もしない
      }
    } catch (error) {
      this._error("画像登録失敗", "画像の登録に失敗しました。", error);
    }
  }

  private static parseAsClaioImageDragParameter(text: string): IImageDragParameter {
    try {
      return JSON.parse(text) as IImageDragParameter;
    } catch (e) {
      console.info("Failed to parse JSON");
      return null;
    }
  }

  public static isImageDragParameter(text: string): boolean {
    if (!text) { return false; }
    const data = ImageDropManager.parseAsClaioImageDragParameter(text);
    return !!data && (data.isClaioImage || data.isClaioSchema || data.isExternalImage || data.isOcojoImage || data.isRegisteredImage);
  }

  private async addMovableImageItemFromFile(file: File): Promise<void> {
    if (!file) { return; }
    FileToBase64TextConverter.convert(
      file,
      (imageText) => {
        this.addMovableImageItem(imageText);
      },
      () => {
      this._error(
        "画像変換失敗",
        "画像の変換に失敗しました。",
        null,
      );
    });
  }

  private createCacheImageSource(imageText: string): Promise<IDocumentImageSource> {
    return this.createImageSource
      ? this.createImageSource(imageText)
      : DomainRegistry.document.getDocumentRepository().createImageSource(imageText);
  }

  private async addMovableImageItem(imageText: string): Promise<void> {
    await this.createCacheImageSource(imageText)
    .then(imageSource => {
      this.addMovableImageItemFromImageSource(imageSource, ImageKinds.Image, null, null, null);
    })
    .catch(e => {
      this._error(
        "画像登録失敗",
        "画像の登録に失敗しました。",
        e,
      );
    });
  }

  private async addMovableImageItemFromClaioImage(patientId: string, claioImageId: string, isVisibleLayer: boolean, isMovie: boolean, isSecret: boolean, departmentId: string): Promise<void> {
    const repository = DomainRegistry.document.getDocumentRepository();
    repository.createImageSourceFromClaioImage(patientId, claioImageId, isVisibleLayer, isMovie, isSecret, departmentId)
    .then(imageSource => {
      const imageKind = isMovie ? ImageKinds.ClaioMovie : ImageKinds.ClaioImage;
      if (isSecret) {
        // TODO: isSecret対応
        console.warn("シークレット画像への対応は未実装です。");
      }
      this.addMovableImageItemFromImageSource(imageSource, imageKind, claioImageId, departmentId, null);
    })
    .catch(e => {
      this._error(
        this.claioImageName + "登録失敗",
        this.claioImageName + "登録に失敗しました。",
        e,
      );
    });
  }

  private async addMovableImageItemFromImageSource(imageSource: IDocumentImageSource, imageKind: ImageKinds, claioImageId: string, departmentId: string, externalImageId: string): Promise<void> {
    const item = await MovableImageItem.fromImageSource(this._position, imageSource, imageKind);
    item.claioImageId = claioImageId;
    item.imageDepartmentId = departmentId;
    item.externalImageId = externalImageId;
    this._addItem(item);
  }

  private async addMovableImageItemFromClaioSchema(templateId: number, groupId: number, itemId: number): Promise<void> {
    const repository = DomainRegistry.document.getDocumentRepository();
    repository.createImageSourceFromClaioSchema(templateId, groupId, itemId)
    .then(imageSource => {
      this.addMovableImageItemFromImageSource(imageSource, ImageKinds.ClaioSchema, null, null, null);
    })
    .catch(e => {
      this._error(
        this.schemaName + "登録失敗",
        this.schemaName + "Claioシェーマの登録に失敗しました。",
        e,
      );
    });
  }

  private async addMovableImageItemFromExternalImage(patientId: string, externalImageId: string): Promise<void> {
    const repository = DomainRegistry.document.getDocumentRepository();
    repository.createImageSourceFromExternalImage(patientId, externalImageId)
    .then(imageSource => {
      this.addMovableImageItemFromImageSource(imageSource, ImageKinds.Image, null, null, externalImageId);
    })
    .catch(e => {
      this._error(
        this.externalImageName + "登録失敗",
        this.externalImageName + "の登録に失敗しました。",
        e,
      );
    });
  }

  private async addMovableImageItemFromOcojoImage(patientId: string, imageId: string, isVisibleLayer: boolean, isMovie: boolean, isSecret: boolean, departmentId: string): Promise<void> {
    // OcojoとClaioで文書に貼り付けた時のパラメータも分けるべきだが、既に運用始まってるので簡単には変えられないので、ここはClaioと同様にしておく.
    // OcojoとClaioを同時に利用したいニーズが出てきたら回避策を検討する事.
    // TODO: Ocojo用のドラッグ実装を追加する.
    const repository = DomainRegistry.document.getDocumentRepository();
    repository.createImageSourceFromClaioImage(patientId, imageId, isVisibleLayer, isMovie, isSecret, departmentId)
    .then(imageSource => {
      const imageKind = isMovie ? ImageKinds.ClaioMovie : ImageKinds.ClaioImage;
      if (isSecret) {
        // TODO: isSecret対応
        console.warn("シークレット画像への対応は未実装です。");
      }
      this.addMovableImageItemFromImageSource(imageSource, imageKind, imageId, departmentId, null);
    })
    .catch(e => {
      this._error(
        this.ocojoImageName + "登録失敗",
        this.ocojoImageName + "登録に失敗しました。",
        e,
      );
    });
  }
}
